import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IbanInput, Loader, ParameterInput, StyledColumn, StyledContent, StyledForm } from "@monorepo/components";
import { servicesApi, useServiceByIdQuery } from "@monorepo/services/api/services";
import { setService } from "@monorepo/slices/service";
import { FormStepsEnum, IResponseError, ParameterTypeEnum, ServiceParameterEnum } from "@monorepo/types";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";

import ServiceListAutocomplete from "components/ServiceListAutocomplete/ServiceListAutocomplete";
import { useAppDispatch, useAppSelector } from "hooks";

interface IFormData {
  [key: string]: string;
}

interface IServiceAccountFormProps {
  id: string;
}

export const ServiceAccountForm: React.FC<IServiceAccountFormProps> = ({ id }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data: service, isLoading } = useServiceByIdQuery(id);
  const methods = useForm();
  const [inputError, setInputError] = useState<string>();
  const serviceSlice = useAppSelector((state) => state.serviceSlice);
  const onSubmit = async (formData: IFormData) => {
    const { data, error } = await dispatch(
      servicesApi.endpoints.getUpdateServiceInformation.initiate({
        service_id: id,
        service_parameters: Object.keys(formData),
        values: Object.values(formData),
      })
    );

    if (!error) {
      if (data) {
        if (!!serviceSlice && !!serviceSlice.parameters && data.parameters) {
          const firstStep = data?.parameters?.find(
            (parameter) => parameter.step === FormStepsEnum.ACCOUNT_VERIFICATION
          );
          const serviceSliceParametersWithoutFirstStep = serviceSlice.parameters.filter(
            (parameter) => parameter.step !== FormStepsEnum.ACCOUNT_VERIFICATION
          );
          if (serviceSliceParametersWithoutFirstStep && !!serviceSliceParametersWithoutFirstStep.length && firstStep) {
            dispatch(
              setService({
                ...serviceSlice,
                parameters: [...serviceSliceParametersWithoutFirstStep, firstStep],
              })
            );
          }
        } else {
          dispatch(setService(data));
        }
      }
    } else if (error) {
      if (!service?.parameters) {
        return;
      }
      const parameter = service.parameters.find(({ step }) => step === FormStepsEnum.ACCOUNT_VERIFICATION);
      if (parameter?.parameter_type === ParameterTypeEnum.IBAN) {
        methods.setError(ServiceParameterEnum.CUSNO, {
          type: "backend",
          message: (error as Array<IResponseError>)?.[0].title,
        });
      } else {
        setInputError((error as Array<IResponseError>)?.[0].title);
      }
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  if (service?.parameters) {
    const parameter = service.parameters.find(({ step }) => step === FormStepsEnum.ACCOUNT_VERIFICATION);
    if (parameter) {
      return (
        <FormProvider {...methods}>
          <StyledForm onSubmit={methods.handleSubmit(onSubmit)}>
            <StyledContent>
              <StyledColumn>
                <ServiceListAutocomplete initialValue={service?.api_service_name} />
                <Box
                  sx={{
                    justifySelf: {
                      xs: "stretch",
                      sm: "center",
                    },
                    width: {
                      xs: "100%",
                      sm: "485px",
                      md: "340px",
                    },
                    overflow: {
                      md: "hidden",
                    },
                    "& > div > div > input": {
                      width: "100%",
                      padding: "6px 16px 3px 3px",
                    },
                  }}
                >
                  {parameter.parameter_type === ParameterTypeEnum.IBAN ? (
                    <IbanInput
                      control={methods.control}
                      key={parameter.parameter_name}
                      registerName={parameter.parameter_name}
                      disabled={parameter.read_only}
                      responseError={inputError}
                    />
                  ) : (
                    <ParameterInput parameter={parameter} error={inputError} autoFocus />
                  )}
                </Box>
                <LoadingButton
                  sx={{ justifySelf: "center" }}
                  disableElevation
                  loading={isLoading || methods.formState.isSubmitting}
                  disabled={isLoading || methods.formState.isSubmitting}
                  variant="contained"
                  type="submit"
                >
                  {t("common.continue")}
                </LoadingButton>
              </StyledColumn>
            </StyledContent>
          </StyledForm>
        </FormProvider>
      );
    }
  }

  return null;
};
